import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { consumeCredits } from '../../../services/creditService';
import { stripeService } from '../../../services/stripeService';
import { createProperty, findPropertyByAddress, getProperty } from '../../../services/propertyService';
import { saveEstimate } from '../../../services/estimateService';
import { useDetailedEstimate } from '../../../hooks/estimation';
import { useCustomPageTitle } from '../../../hooks/usePageTitle';
import { useUserProfile } from '../../../hooks/useUserProfile';
import { useEstimationForm } from '../../../contexts/EstimationFormContext';
import { DETAILED_STEPS } from './config';
import { ITEM_DEPENDENCIES, SuggestionStrength } from '../../../data/dependencies';
import { AI_CATALOG } from '../../../data/ai-catalog';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ConfirmDialog from '../../ui/ConfirmDialog';
import FormStepNav from './FormStepNav';

interface DetailedEstimateProps {
  initialData?: any;
  isEditMode?: boolean;
  estimateId?: string;
}

export const DetailedEstimate: React.FC<DetailedEstimateProps> = ({ initialData: propInitialData, isEditMode = false, estimateId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useCustomPageTitle('Estimation détaillée');
  const location = useLocation();
  const { profile, loading: profileLoading } = useUserProfile(user?.uid);
  const ctx = useEstimationForm();

  const [accessInfo, setAccessInfo] = useState<{
    hasCredits: boolean;
    isUnlimited: boolean;
    credits: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFirstPageConfirm, setShowFirstPageConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [dismissedAIBanner, setDismissedAIBanner] = useState(false);

  // ── AI pre-selection from /estimation/assistant ──────────────────────────
  // Read location.state once at mount. Only active on fresh estimations
  // (not in edit mode, not when a propInitialData is passed).
  const aiState = location.state as {
    fromAI?: boolean;
    aiPreselectedItems?: Array<{ key: string; reason: string; strength: 'recommended' | 'optional' }>;
    existingPropertyId?: string;
    prefillAddress?: { address: string; postcode: string; city: string };
  } | null;
  const fromAI = !isEditMode && !propInitialData && aiState?.fromAI === true;
  const aiPreselectedItems = fromAI ? (aiState?.aiPreselectedItems ?? []) : [];
  // If the user selected an existing property in the AI assistant, skip step 1 and reuse it
  const existingPropertyId = !isEditMode && !propInitialData ? (aiState?.existingPropertyId ?? null) : null;
  // If the user selected an orphan estimate, pre-fill address fields at step 1 (credit still consumed)
  const prefillAddress = fromAI ? (aiState?.prefillAddress ?? null) : null;

  // Build the per-category preload map once (stable useMemo with empty deps)
  const aiPreloadData = useMemo(() => {
    if (!fromAI || aiPreselectedItems.length === 0) return null;
    const map: Record<string, Record<string, { selected: boolean; value: number }>> = {};
    for (const item of aiPreselectedItems) {
      const cat = AI_CATALOG.find(c => c.key === item.key)?.category;
      if (!cat) continue;
      if (!map[cat]) map[cat] = {};
      map[cat][item.key] = { selected: true, value: 0 };
    }
    return map;
  }, []); // eslint-disable-line — location.state is stable after mount

  const [initialData] = useState(() => {
    if (propInitialData) return propInitialData;
    // Coming from AI assistant with an existing property selected:
    // provide a minimal initialData so useDetailedEstimate starts at step 2
    // and checkAccess resolves the property without consuming a credit.
    if (existingPropertyId) return { propertyId: existingPropertyId };
    const savedData = localStorage.getItem('editEstimate');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (!parsedData._editMode) {
          localStorage.removeItem('editEstimate');
          const { _editMode, _estimateId, ...cleanData } = parsedData;
          return cleanData;
        }
      } catch {}
    }
    return undefined;
  });

  const {
    currentStep,
    completedSteps,
    formData,
    draftEstimateId,
    setDraftEstimateId,
    autosaveStatus,
    handleStepClick,
    handleNext: baseHandleNext,
    handleStepDataChange,
  } = useDetailedEstimate(initialData, isEditMode, estimateId, user?.uid);

  // ── Inject AI pre-selected items into formData once after mount ──────────
  // Uses a ref flag so the injection runs exactly once, even in StrictMode.
  // handleStepDataChange is called per category — description step untouched,
  // so the hook still starts at step 0 and the user fills in address/type/area.
  const aiInjectedRef = useRef(false);
  useEffect(() => {
    if (!aiPreloadData && !prefillAddress) return;
    if (aiInjectedRef.current) return;
    aiInjectedRef.current = true;
    // Pre-fill address fields for orphan estimates (does not mark step as completed)
    if (prefillAddress) {
      handleStepDataChange('description', { location: prefillAddress });
    }
    // Inject AI item pre-selections per category
    if (aiPreloadData) {
      Object.entries(aiPreloadData).forEach(([cat, catData]) => {
        handleStepDataChange(cat, catData);
      });
    }
  }, [aiPreloadData, prefillAddress, handleStepDataChange]); // eslint-disable-line

  // Agrège toutes les suggestions cross-step depuis l'ensemble de formData.
  // Recalculé dès qu'une case est cochée dans n'importe quelle catégorie.
  const SKIP_CATEGORIES = new Set(['description', 'resultat']);
  const crossStepSuggestions = useMemo(() => {
    const result: Record<string, { strength: SuggestionStrength; reason: string }> = {};
    Object.entries(formData).forEach(([categoryId, categoryData]) => {
      if (SKIP_CATEGORIES.has(categoryId)) return;
      if (!categoryData || typeof categoryData !== 'object') return;
      Object.entries(categoryData as Record<string, any>).forEach(([key, sel]) => {
        if ((sel as any)?.selected) {
          ITEM_DEPENDENCIES[key]?.suggests.forEach(s => {
            if (!result[s.targetKey] ||
                (result[s.targetKey].strength === 'optional' && s.strength === 'recommended')) {
              result[s.targetKey] = { strength: s.strength, reason: s.reason };
            }
          });
        }
      });
    });
    return result;
  }, [formData]); // eslint-disable-line

  // Sync state to context (including aiSteps so the sidebar always has the current value)
  useEffect(() => {
    ctx?.update({
      isActive: true,
      currentStep,
      completedSteps,
      isEditMode,
      formAddress: formData.description?.location?.address || '',
      formPostcode: formData.description?.location?.postcode || '',
      formCity: formData.description?.location?.city || '',
      aiSteps: aiPreloadData ? Object.keys(aiPreloadData) : [],
    });
  }, [currentStep, completedSteps, isEditMode, formData.description?.location?.address, formData.description?.location?.postcode, formData.description?.location?.city]); // eslint-disable-line

  // Sync autosave status to context
  useEffect(() => {
    ctx?.update({ autosaveStatus });
  }, [autosaveStatus]); // eslint-disable-line

  // Register step click handler
  useEffect(() => {
    if (ctx) ctx.onStepClickRef.current = handleStepNavigation;
  });

  // Mark inactive on unmount
  useEffect(() => {
    return () => {
      ctx?.update({ isActive: false, currentStep: '', completedSteps: [], formAddress: '', formPostcode: '', formCity: '', aiSteps: [] });
    };
  }, []); // eslint-disable-line

  const getEstimateKey = () => {
    if (isEditMode && estimateId) return `estimate_paid_${estimateId}`;
    if (formData.description?.location?.address) {
      return `estimate_paid_${btoa(formData.description.location.address)}`;
    }
    return null;
  };

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        navigate('/login', {
          state: { from: location, message: "Pour accéder à l'estimation détaillée, veuillez vous connecter ou créer un compte" }
        });
        return;
      }

      if (isPaid) {
        if (!accessInfo) setAccessInfo({ hasCredits: true, isUnlimited: false, credits: 1 });
        setLoading(false);
        return;
      }

      const checkCreditsAndRedirect = async () => {
        const creditsInfo = await stripeService.getUserCreditsInfo();

        if (creditsInfo === null) {
          if (profileLoading) return;
          const hasLocalCredits = profile?.credits && profile.credits > 0;
          const hasLocalSubscription = profile?.subscriptionStatus && ['active', 'trialing'].includes(profile.subscriptionStatus);
          if (!hasLocalCredits && !hasLocalSubscription) {
            setError("Impossible de vérifier vos crédits. Veuillez vérifier votre connexion et réessayer.");
            setLoading(false);
            return;
          }
          setAccessInfo({
            hasCredits: !!(hasLocalCredits || hasLocalSubscription),
            isUnlimited: !!hasLocalSubscription,
            credits: profile?.credits || 0
          });
        } else {
          setAccessInfo(creditsInfo);
        }

        const finalAccessInfo = creditsInfo || {
          hasCredits: !!(profile?.credits && profile.credits > 0) || !!(profile?.subscriptionStatus && ['active', 'trialing'].includes(profile.subscriptionStatus)),
          isUnlimited: !!(profile?.subscriptionStatus && ['active', 'trialing'].includes(profile.subscriptionStatus)),
          credits: profile?.credits || 0
        };

        if (!finalAccessInfo.hasCredits && !finalAccessInfo.isUnlimited && !isPaid) {
          navigate('/credits/purchase', { state: { message: "Un crédit ou un abonnement est nécessaire pour réaliser une estimation détaillée" } });
          return;
        }
      };

      try {
        const estimateKey = getEstimateKey();
        if (estimateKey) {
          const savedIsPaid = localStorage.getItem(estimateKey);
          if (savedIsPaid === 'true') {
            setIsPaid(true);
            setAccessInfo({ hasCredits: true, isUnlimited: false, credits: 1 });
            setLoading(false);
            return;
          }
        }

        if (isEditMode && estimateId) {
          setIsPaid(true);
          setAccessInfo({ hasCredits: true, isUnlimited: false, credits: 1 });
        } else if (initialData?.propertyId && user) {
          const property = await getProperty(user.uid, initialData.propertyId);
          if (property) {
            setIsPaid(true);
            setAccessInfo({ hasCredits: true, isUnlimited: false, credits: 1 });
            // Inject property data into formData so the description step
            // (and result page / autosave) has address, type and area.
            handleStepDataChange('description', {
              location: {
                address: property.address,
                postcode: property.postcode,
                city: property.city,
              },
              propertyType: property.type,
              area: property.area,
              propertyId: property.id,
            });
          } else {
            await checkCreditsAndRedirect();
          }
        } else {
          await checkCreditsAndRedirect();
        }
      } catch {
        setError("Impossible de vérifier vos droits d'accès");
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, navigate, location, initialData, formData.description?.location?.address, profile, profileLoading, isPaid]); // eslint-disable-line

  const handleStepNavigation = (nextStep: string) => {
    if (currentStep === 'description' && !completedSteps.includes('description') && !isPaid && !accessInfo?.isUnlimited) {
      setPendingNavigation(nextStep);
      setShowFirstPageConfirm(true);
      return;
    }
    handleStepClick(nextStep);
  };

  const handleNext = () => {
    const nextStep = DETAILED_STEPS[DETAILED_STEPS.findIndex(s => s.id === currentStep) + 1].id;
    handleStepNavigation(nextStep);
  };

  const handleBack = () => {
    if (currentStep === 'preparation' && completedSteps.includes('description')) return;
    const currentIndex = DETAILED_STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      const prevStep = DETAILED_STEPS[currentIndex - 1].id;
      handleStepNavigation(prevStep);
    }
  };

  const confirmFirstPage = async () => {
    if (isConfirming) return;
    if (!user || !formData.description?.location?.address) {
      setError("Informations incomplètes");
      setShowFirstPageConfirm(false);
      return;
    }
    setIsConfirming(true);

    const { address, postcode, city } = formData.description.location;
    const { propertyType, area } = formData.description;

    if (!address || !postcode || !city || !propertyType || !area) {
      setError("Informations du bien incomplètes");
      setShowFirstPageConfirm(false);
      return;
    }

    try {
      const existingProperty = await findPropertyByAddress(user.uid, address, postcode);
      if (existingProperty) {
        setError("Une propriété existe déjà pour cette adresse. Veuillez l'utiliser depuis votre dashboard.");
        setShowFirstPageConfirm(false);
        setTimeout(() => navigate('/dashboard'), 3000);
        return;
      }

      if (accessInfo?.isUnlimited) {
        const estimateKey = getEstimateKey();
        if (estimateKey) { localStorage.setItem(estimateKey, 'true'); }
        setIsPaid(true);

        const propertyId = await createProperty(user.uid, {
          userId: user.uid, address, postcode, city, type: propertyType, area,
          createdAt: new Date().toISOString(), lastEstimateAt: null, estimateId: null, creditConsumed: true
        });
        handleStepDataChange('description', { ...formData.description, propertyId });

        // Create draft estimate
        const draftId = await saveEstimate(user.uid, {
          type: 'detailed',
          status: 'draft',
          propertyId,
          description: { ...formData.description, propertyId },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        setDraftEstimateId(draftId);
        setSuccessMessage("Accès confirmé ! Vous pouvez maintenant continuer votre estimation avec votre abonnement illimité.");
        setTimeout(() => setSuccessMessage(null), 3000);
        setShowFirstPageConfirm(false);
        setError(null);
        if (pendingNavigation) { handleStepClick(pendingNavigation); setPendingNavigation(null); } else { baseHandleNext(); }
        return;
      }

      const estimateKey = getEstimateKey();
      if (estimateKey) { localStorage.setItem(estimateKey, 'true'); }
      setIsPaid(true);

      await consumeCredits(user.uid, 1, { operationType: 'detailed_estimate', address });

      const propertyId = await createProperty(user.uid, {
        userId: user.uid, address, postcode, city, type: propertyType, area,
        createdAt: new Date().toISOString(), lastEstimateAt: null, estimateId: null, creditConsumed: true
      });
      handleStepDataChange('description', { ...formData.description, propertyId });

      // Create draft estimate
      const draftId = await saveEstimate(user.uid, {
        type: 'detailed',
        status: 'draft',
        propertyId,
        description: { ...formData.description, propertyId },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setDraftEstimateId(draftId);

      if (accessInfo) {
        const newCredits = Math.max(0, accessInfo.credits - 1);
        setAccessInfo({ ...accessInfo, credits: newCredits, hasCredits: true });
      }

      setSuccessMessage("Crédit consommé avec succès ! Vous pouvez maintenant continuer votre estimation.");
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowFirstPageConfirm(false);
      setError(null);
      if (pendingNavigation) { handleStepClick(pendingNavigation); setPendingNavigation(null); } else { baseHandleNext(); }

    } catch (err) {
      setShowFirstPageConfirm(false);
      if (err instanceof Error) {
        if (err.message === 'Solde de crédits insuffisant' || err.message === 'INSUFFICIENT_CREDITS') {
          navigate('/credits/purchase', { state: { message: "Vous n'avez plus de crédits disponibles" } });
        } else {
          setError("Une erreur est survenue lors de la validation: " + err.message);
        }
      } else {
        setError("Une erreur inconnue est survenue lors de la validation");
      }
    } finally {
      setIsConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <FormStepNav />
      <div style={{ maxWidth: 768, margin: '0 auto', padding: '24px 16px' }}>
        {successMessage && (
          <div
            className="flex items-center gap-2 mb-4"
            style={{
              padding: '8px 14px',
              background: '#F0FDF4',
              border: '0.5px solid rgba(5,150,105,0.3)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              color: '#059669',
            }}
          >
            {successMessage}
          </div>
        )}

        {error && (
          <div
            className="mb-4"
            style={{
              padding: '8px 14px',
              background: '#FEF2F2',
              border: '0.5px solid rgba(220,38,38,0.3)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              color: '#DC2626',
            }}
          >
            {error}
          </div>
        )}

        {/* AI pre-selection banner — first step after description only */}
        {fromAI && currentStep === DETAILED_STEPS[1].id && !dismissedAIBanner && (
          <div
            className="flex items-center gap-2 mb-4"
            style={{
              padding: '10px 14px',
              background: '#EEEDFE',
              border: '0.5px solid #AFA9EC',
              borderRadius: 'var(--radius-md)',
              fontSize: 12,
              color: '#3C3489',
            }}
          >
            <Sparkles size={14} strokeWidth={1.75} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              SimulReno AI a pré-sélectionné{' '}
              <strong>{aiPreselectedItems.length} poste{aiPreselectedItems.length > 1 ? 's' : ''} de travaux</strong>.
              Vérifiez et ajustez selon votre situation.
            </span>
            <button
              onClick={() => setDismissedAIBanner(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 2,
                color: '#3C3489',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
              aria-label="Fermer"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
        )}

        <div
          style={{
            background: 'white',
            border: '0.5px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-card)',
            padding: '24px',
          }}
        >
          {React.createElement(DETAILED_STEPS.find(s => s.id === currentStep)!.component, {
            data: currentStep === 'resultat' ? formData : formData[currentStep],
            onChange: (data: any) => handleStepDataChange(currentStep, data),
            onNext: handleNext,
            onBack: handleBack,
            crossStepSuggestions,
            ...(currentStep === 'resultat' && { isEditMode, estimateId, draftEstimateId })
          })}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showFirstPageConfirm}
        title={accessInfo?.isUnlimited ? "Confirmation d'accès" : "Votre crédit a été utilisé"}
        message={accessInfo?.isUnlimited
          ? "Votre bien immobilier sera disponible depuis votre dashboard. Avec votre abonnement illimité, vous pourrez faire autant d'estimations que vous le souhaitez."
          : "Votre bien immobilier sera disponible depuis votre dashboard. Vous pourrez désormais faire autant d'estimations détaillées que vous le souhaitez sur ce bien."
        }
        confirmLabel="Confirmer"
        onConfirm={confirmFirstPage}
        onCancel={() => { setShowFirstPageConfirm(false); setPendingNavigation(null); }}
        variant="warning"
        disabled={isConfirming}
      />
    </>
  );
};

export default DetailedEstimate;
